import React from 'react';
import { CurrencyAmount, Token } from '@uniswap/sdk-core';
import {
  Box, Button, ButtonProps, styled, Typography,
} from '@mui/material';
import { TokenInputError, TokenWithLabel } from 'common-client';
import { PriceInputField } from './PriceInputField';

const BackButton = styled(Button, { name: 'BackButton' })<ButtonProps>(() => ({
  borderRadius: '24px',
  textTransform: 'capitalize',
}));

const NextButton = styled(Button, { name: 'NextButton' })<ButtonProps>(() => ({
  borderRadius: '24px',
  textTransform: 'capitalize',
}));

export interface SelectPriceRangeBoxProps {
  /**
   * Prompt to display
   */
  prompt?: string;
  /**
   * Sell asset
   */
  sellAsset: Token | null;
  /**
   * Minimum price
   */
  minimumPrice: CurrencyAmount<Token> | null;
  /**
   * Callback for selecting minimum price
   */
  onMinimumPriceUpdate?: (minimumPrice: CurrencyAmount<Token>) => void;
  /**
   * Tick width
   */
  tickWidth: CurrencyAmount<Token> | null;
  /**
   * Callback for setting tick-width
   */
  onTickWidthUpdate?: (tickWidth: CurrencyAmount<Token>) => void;
  /**
   * Maximum price
   */
  maximumPrice: CurrencyAmount<Token> | null;
  /**
   * Callback on clicking back button
   */
  onBackClick?: () => void;
  /**
   * Callback on clicking next button
   */
  onNextClick?: () => void;
}

export function SelectPriceRangeBox({
  prompt,
  sellAsset,
  minimumPrice,
  onMinimumPriceUpdate,
  tickWidth,
  onTickWidthUpdate,
  maximumPrice,
  onBackClick,
  onNextClick,
}: SelectPriceRangeBoxProps) {
  const [minimumPriceError, setMinimumPriceError] = React.useState<TokenInputError>(
    TokenInputError.NoError,
  );
  const [tickWidthError, setTickWidthError] = React.useState<TokenInputError>(
    TokenInputError.NoError,
  );
  const minimumPriceZeroMessage = (!minimumPrice || minimumPrice.equalTo('0')) ? 'Minimum price cannot be zero' : undefined;
  const tickWidthZeroMessage = (!tickWidth || tickWidth.equalTo('0')) ? 'Tick width cannot be zero' : undefined;
  return (
    <Box
      display="flex"
      flexDirection="column"
      flexWrap="nowrap"
    >
      <Typography variant="h3" fontWeight={700} fontSize="24px" lineHeight="32px" color="textPrimary" my="8px">
        Select a Price Range
      </Typography>
      {prompt && (
        <Typography variant="body1" fontWeight={400} fontSize="16px" lineHeight="24px" color="textSecondary" mb="16px">
          {prompt}
        </Typography>
      )}
      <Box alignSelf="center">
        <TokenWithLabel token={sellAsset} />
      </Box>
      <Typography variant="body1" fontWeight={700} fontSize="16px" lineHeight="24px" color="textPrimary" mb="8px">
        Minimum Price
      </Typography>
      <Box mb="24px">
        <PriceInputField
          label="Minimum Price"
          amount={minimumPrice}
          onUpdateAmount={(amount, error) => {
            if (amount) {
              onMinimumPriceUpdate?.(amount);
            }
            setMinimumPriceError(error);
          }}
          error={minimumPriceZeroMessage}
        />
      </Box>
      <Typography variant="body1" fontWeight={700} fontSize="16px" lineHeight="24px" color="textPrimary" mb="8px">
        Tick Width
      </Typography>
      <Box mb="24px">
        <PriceInputField
          label="Tick Width"
          amount={tickWidth}
          onUpdateAmount={(amount, error) => {
            if (amount) {
              onTickWidthUpdate?.(amount);
            }
            setTickWidthError(error);
          }}
          error={tickWidthZeroMessage}
        />
      </Box>
      <Typography variant="body1" fontWeight={700} fontSize="16px" lineHeight="24px" color="textPrimary" mb="8px">
        Maximum Price
      </Typography>
      <Box mb="24px">
        <PriceInputField
          label="Maximum Price"
          amount={maximumPrice}
          disabled
        />
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        flexWrap="nowrap"
        gap="16px"
      >
        <BackButton
          color="secondary"
          variant="contained"
          onClick={onBackClick}
          fullWidth
        >
          <Typography variant="body1" fontWeight={600} fontSize="14px" lineHeight="24px">
            Back
          </Typography>
        </BackButton>
        <NextButton
          color="primary"
          variant="contained"
          onClick={onNextClick}
          fullWidth
          disabled={!minimumPrice || !tickWidth
            || !!minimumPriceZeroMessage || !!tickWidthZeroMessage
            || minimumPriceError !== TokenInputError.NoError
            || tickWidthError !== TokenInputError.NoError}
        >
          <Typography>
            Next
          </Typography>
        </NextButton>
      </Box>
    </Box>
  );
}
